import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const FAQ_ITEMS = [
  {
    question: 'How do I book a service?',
    answer: 'Browse services on the Explore page, select one you like, and tap Book Now. Choose your preferred date and add any notes for the provider.',
  },
  {
    question: 'How do I become a provider?',
    answer: 'Sign up as a Provider account type, complete your profile setup, and start creating service listings from your Account page.',
  },
  {
    question: 'How do payments work?',
    answer: 'Payments are processed securely through the platform. Providers receive earnings after service completion, minus a 15% platform commission.',
  },
  {
    question: 'How do I get verified?',
    answer: 'Go to Account → Get Verified. Upload a government ID and portfolio samples. Verification is typically reviewed within 24 hours.',
  },
  {
    question: 'Can I cancel a booking?',
    answer: 'Yes, you can cancel pending bookings from the Orders page. Select the booking and choose a cancellation reason.',
  },
  {
    question: 'How do I contact a provider?',
    answer: 'You can message any provider directly from their profile or service listing page using the Message button.',
  },
];

export default function HelpPage() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return FAQ_ITEMS;
    const q = search.toLowerCase();
    return FAQ_ITEMS.filter(
      (item) =>
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q)
    );
  }, [search]);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-dark-subtext">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h1 className="text-xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>Help &amp; Support</h1>
      </div>

      <div className="px-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-dark-subtext" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search FAQ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-dark-surface border border-dark-border rounded-2xl py-3 pl-11 pr-4 text-sm text-dark-text placeholder-dark-subtext focus:outline-none focus:border-brand-purple/50"
          />
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <p className="text-dark-text font-semibold text-base mb-1">No results found</p>
              <p className="text-dark-subtext text-sm">Try a different search term.</p>
            </div>
          ) : (
            filtered.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={i} className="bg-dark-surface border border-dark-border rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggle(i)}
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                  >
                    <span className="text-sm font-semibold text-dark-text pr-3">{item.question}</span>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`text-dark-subtext flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-0">
                      <p className="text-dark-subtext text-sm leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Contact Section */}
        <div className="bg-dark-surface border border-dark-border rounded-2xl p-4 mt-4">
          <h2 className="text-base font-bold text-dark-text mb-3">Still need help?</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-dark-bg flex items-center justify-center text-base">📧</div>
              <div>
                <p className="text-dark-subtext text-xs">Email</p>
                <a href="mailto:support@joinfindrapp.com" className="text-brand-purple text-sm font-medium">
                  support@joinfindrapp.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-dark-bg flex items-center justify-center text-base">📸</div>
              <div>
                <p className="text-dark-subtext text-xs">Instagram</p>
                <a
                  href="https://instagram.com/joinfindr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-purple text-sm font-medium"
                >
                  @joinfindr
                </a>
              </div>
            </div>
            <p className="text-dark-subtext text-xs pt-1">Response time: Within 24 hours</p>
          </div>
        </div>
      </div>
    </div>
  );
}
